import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  ElementRef,
  inject,
  QueryList,
  ViewChildren,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { WavesComponent } from '@core/components/waves/waves.component';
import { FAQ_DATA_ES, FAQ_DATA_EN } from '@core/data/faq.data';
import { SERVICES_DATA_ES, SERVICES_DATA_EN } from '@core/data/services.data';
import { PaymentRecoveryBannerComponent } from '@core/components/payment-recovery-banner/payment-recovery-banner.component';
import { ClientStore } from '@core/state/client/client.store';
import { ContactFormComponent } from '@features/home/components/contact-form/contact-form.component';
import { ServiceCardComponent } from '@features/home/components/service-card/service-card.component';
import { SubscriberFormComponent } from '@features/home/components/subscriber-form/subscriber-form.component';
import { WidgetSearchComponent } from '@features/home/components/widget-search/widget-search.component';
import { AccordionModule } from 'primeng/accordion';
import { ButtonModule } from 'primeng/button';
import { CarouselModule } from 'primeng/carousel';

@Component({
  selector: 'd2d-home',
  standalone: true,
  imports: [
    AccordionModule,
    CommonModule,
    WidgetSearchComponent,
    ServiceCardComponent,
    ButtonModule,
    CarouselModule,
    ContactFormComponent,
    SubscriberFormComponent,
    WavesComponent,
    TranslocoPipe,
    PaymentRecoveryBannerComponent,
  ],
  templateUrl: './home.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './home.component.scss',
})
export class HomeComponent implements OnInit {
  private readonly clientStore = inject(ClientStore);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly translocoService = inject(TranslocoService);
  public clientConfig = this.clientStore.clientConfig;

  @ViewChildren('panelEl', { read: ElementRef })
  panelElements!: QueryList<ElementRef>;

  protected activePanel = -1; // ← o -1 si quieres que inicie cerrado
  protected showSubscriberForm = false;
  protected responsiveOptions:
    | {
        breakpoint: string;
        numVisible: number;
        numScroll: number;
      }[]
    | undefined;
  protected faqs = computed(() =>
    this.translocoService.activeLang() === 'en' ? FAQ_DATA_EN : FAQ_DATA_ES,
  );
  protected affiliates: { name: string; image: string }[] = [];
  protected services = computed(() =>
    this.translocoService.activeLang() === 'en' ? SERVICES_DATA_EN : SERVICES_DATA_ES,
  );

  ngOnInit(): void {
    this.setResponsiveOptions();
    this.setAffiliates();
  }

  onPanelChange(event: string | number | string[] | number[]): void {
    const realIndex = Array.isArray(event)
      ? parseInt(event[0] as string)
      : parseInt(event as string);

    // Scroll al contenido del panel
    setTimeout(() => {
      const el = document.getElementById(`faq-${realIndex}`);
      if (el) {
        window.scrollTo({
          top: el.getBoundingClientRect().top + window.scrollY - 250,
          behavior: 'smooth',
        });
      }
    }, 300);
  }

  setResponsiveOptions(): void {
    this.responsiveOptions = [
      {
        breakpoint: '1400px',
        numVisible: 2,
        numScroll: 1,
      },
      {
        breakpoint: '1199px',
        numVisible: 3,
        numScroll: 1,
      },
      {
        breakpoint: '767px',
        numVisible: 2,
        numScroll: 1,
      },
      {
        breakpoint: '575px',
        numVisible: 1,
        numScroll: 1,
      },
    ];
  }

  setAffiliates(): void {
    this.affiliates = [
      {
        image: './icons/affiliates/h10.png',
        name: 'H10 Hotels',
      },
      {
        image: './icons/affiliates/melia.png',
        name: 'Melia Hotels',
      },
      {
        image: './icons/affiliates/spirit.png',
        name: 'Spirit Hotels',
      },
      {
        image: './icons/affiliates/m.png',
        name: 'Riu Hotels',
      },
    ];
  }

  getSanitizedHTML(content: string) {
    return this.sanitizer.bypassSecurityTrustHtml(content);
  }
}
